import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Payment,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const ManageArrears = () => {
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState('');
  const [studentInfo, setStudentInfo] = useState(null);
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [remarks, setRemarks] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  const searchStudent = async () => {
    if (!studentId) {
      setAlert({ show: true, message: 'Please enter student ID', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Find student
      const studentsResponse = await axios.get(`${API_URL}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const student = studentsResponse.data.find(s => s.student_id === studentId);
      
      if (!student) {
        setAlert({ show: true, message: 'Student not found', severity: 'error' });
        setStudentInfo(null);
        setOutstandingPayments([]);
        return;
      }

      setStudentInfo(student);

      // Get outstanding fees
      const feesResponse = await axios.get(`${API_URL}/fees/student/${student.id}/outstanding`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOutstandingPayments(feesResponse.data.outstanding_payments || []);
      
      if (feesResponse.data.outstanding_payments.length === 0) {
        setAlert({ show: true, message: 'No outstanding payments found', severity: 'info' });
      }
    } catch (error) {

      setAlert({ show: true, message: 'Error loading student data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        return prev.filter(id => id !== paymentId);
      } else {
        return [...prev, paymentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPayments.length === outstandingPayments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(outstandingPayments.map(p => p.id));
    }
  };

  const getTotalAmount = () => {
    return outstandingPayments
      .filter(p => selectedPayments.includes(p.id))
      .reduce((sum, p) => sum + parseFloat(p.amount), 0);
  };

  const handleMarkBulkPayment = async () => {
    if (selectedPayments.length === 0) {
      setAlert({ show: true, message: 'Please select at least one payment', severity: 'warning' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const selectedWeeks = outstandingPayments
        .filter(p => selectedPayments.includes(p.id))
        .map(p => ({
          week_start_date: p.week_start_date,
          week_end_date: p.week_end_date,
          amount: p.amount
        }));

      await axios.post(`${API_URL}/fees/mark-bulk-payment`, {
        student_id: studentInfo.id,
        class_id: studentInfo.class_id,
        weeks: selectedWeeks,
        payment_method: paymentMethod,
        remarks: remarks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlert({ show: true, message: `Successfully marked ${selectedPayments.length} payments!`, severity: 'success' });
      setOpenPaymentDialog(false);
      setSelectedPayments([]);
      setRemarks('');
      searchStudent(); // Refresh data
    } catch (error) {
      setAlert({ show: true, message: 'Error marking payments', severity: 'error' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/fund-collector/dashboard')}
            sx={{ color: 'white', mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1
              }}
            >
              Manage Arrears
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Pay multiple weeks at once for students with outstanding fees
            </Typography>
          </Box>
        </Box>

        {/* Alert */}
        {alert.show && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Search Section */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Search Student
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Student ID"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                placeholder="Enter student ID"
                onKeyPress={(e) => e.key === 'Enter' && searchStudent()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={searchStudent}
                disabled={loading}
                sx={{
                  height: '56px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                <Search sx={{ mr: 1 }} />
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Student Info */}
        {studentInfo && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Student Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Student ID</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {studentInfo.student_id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {studentInfo.user?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Class</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {studentInfo.class?.name} - {studentInfo.class?.section}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Outstanding Payments */}
        {outstandingPayments.length > 0 && (
          <Paper
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Outstanding Payments ({outstandingPayments.length})
              </Typography>
              {selectedPayments.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Selected: {selectedPayments.length} | Total: Rs. {getTotalAmount().toFixed(2)}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setOpenPaymentDialog(true)}
                    sx={{
                      mt: 1,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                  >
                    <Payment sx={{ mr: 1 }} />
                    Mark Selected as Paid
                  </Button>
                </Box>
              )}
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPayments.length === outstandingPayments.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Week</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Class</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outstandingPayments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPayments.includes(payment.id)}
                          onChange={() => handleSelectPayment(payment.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(payment.week_start_date).toLocaleDateString()} -{' '}
                        {new Date(payment.week_end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>Rs. {parseFloat(payment.amount).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status}
                          color={payment.payment_status === 'overdue' ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{payment.class_room?.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Payment Dialog */}
        <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Confirm Bulk Payment
            <IconButton
              onClick={() => setOpenPaymentDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Student:</strong> {studentInfo?.user?.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Number of Weeks:</strong> {selectedPayments.length}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>Total Amount:</strong> Rs. {getTotalAmount().toFixed(2)}
              </Typography>

              <TextField
                select
                fullWidth
                label="Payment Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                sx={{ mb: 2 }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="online">Online Payment</option>
              </TextField>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks (Optional)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleMarkBulkPayment}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              Confirm Payment
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ManageArrears;
